import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DocumentDuplicateIcon, 
  ArrowDownTrayIcon,
  EyeIcon,
  ShareIcon,
  Cog6ToothIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../config';
import { downloadBadgeAsImage, copyBadgeUrl } from '../utils/downloadUtils';
import { downloadBadgeFromPreview } from '../utils/imageDownload';
import { downloadBadgeSimple } from '../utils/simpleDownload';

interface Speaker {
  id: string;
  name: string;
  title?: string;
  company?: string;
  image?: string;
}

interface BannerConfig {
  theme: string;
  size: string;
  format: string;
  showTitle: boolean;
  showCompany: boolean;
  watermark: boolean;
  customText: string;
}

const SpeakerBannerGenerator: React.FC = () => {
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [selectedSpeaker, setSelectedSpeaker] = useState<string>('');
  const [config, setConfig] = useState<BannerConfig>({
    theme: 'gradient',
    size: 'large',
    format: 'banner',
    showTitle: true,
    showCompany: true,
    watermark: true,
    customText: ''
  });
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  useEffect(() => {
    const fetchSpeakers = async () => {
      try {
        console.log('Fetching speakers from API...');
        const response = await fetch(`${API_BASE_URL}/public/speakers?limit=1000`);
        console.log('Response status:', response.status);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Speakers data received:', data);
        console.log('Number of speakers:', data.speakers?.length || 0);
        
        setSpeakers(data.speakers || []);
        if (data.speakers && data.speakers.length > 0) {
          setSelectedSpeaker(data.speakers[0].id);
          console.log('Selected first speaker:', data.speakers[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch speakers:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSpeakers();
  }, []);

  useEffect(() => {
    if (selectedSpeaker) {
      const params = new URLSearchParams();
      params.set('theme', config.theme);
      params.set('size', config.size);
      params.set('format', config.format);
      params.set('title', config.showTitle.toString());
      params.set('company', config.showCompany.toString());
      params.set('watermark', config.watermark.toString());
      
      if (config.customText) params.set('text', config.customText);

      setPreviewUrl(`/speaker-banner/${selectedSpeaker}?${params.toString()}`);
    }
  }, [selectedSpeaker, config]);

  const generateShareableUrl = () => {
    return `${window.location.origin}${previewUrl}`;
  };

  const handleCopyUrl = () => {
    const success = copyBadgeUrl(generateShareableUrl());
    if (success) {
      console.log('URL copied to clipboard');
    }
  };

  const handleDownload = async () => {
    const speaker = speakers.find(s => s.id === selectedSpeaker);
    const filename = speaker ? `${speaker.name.replace(/[^a-zA-Z0-9]/g, '-')}-speaker-banner.png` : 'speaker-banner.png';
    
    try {
      await downloadBadgeSimple(generateShareableUrl(), filename);
    } catch (error) {
      console.error('Simple download failed, trying iframe method:', error);
      try {
        await downloadBadgeFromPreview(generateShareableUrl(), filename);
      } catch (error2) {
        console.error('Iframe download failed, falling back to manual method:', error2);
        downloadBadgeAsImage(generateShareableUrl(), filename);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00529B]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Speaker Banner Generator
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Create personalized "I'll be speaking at Language Intelligence 2025" banners for social media promotion. 
            Perfect for LinkedIn, Twitter, and other platforms.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Configuration Panel */}
          <div className="lg:col-span-1 space-y-6">
            {/* Speaker Selection */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <UserIcon className="w-5 h-5 mr-2" />
                Speaker Selection ({speakers.length} speakers loaded)
              </h3>
              <select
                value={selectedSpeaker}
                onChange={(e) => setSelectedSpeaker(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00529B] focus:border-transparent"
              >
                <option value="">Select a speaker...</option>
                {speakers.map((speaker) => (
                  <option key={speaker.id} value={speaker.id}>
                    {speaker.name} {speaker.company && `(${speaker.company})`}
                  </option>
                ))}
              </select>
            </div>

            {/* Theme Selection */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Theme</h3>
              <div className="space-y-2">
                {[
                  { value: 'gradient', label: 'Conference Gradient', preview: 'bg-gradient-to-r from-[#00529B] to-[#4A90E2]' },
                  { value: 'blue', label: 'Conference Blue', preview: 'bg-[#00529B]' },
                  { value: 'dark', label: 'Professional Dark', preview: 'bg-gray-800' },
                  { value: 'minimal', label: 'Clean Minimal', preview: 'bg-white border' },
                  { value: 'keynote', label: 'Keynote Gold', preview: 'bg-gradient-to-r from-yellow-400 to-orange-500' }
                ].map((theme) => (
                  <label key={theme.value} className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="theme"
                      value={theme.value}
                      checked={config.theme === theme.value}
                      onChange={(e) => setConfig({ ...config, theme: e.target.value })}
                      className="w-4 h-4 text-[#00529B] focus:ring-[#00529B]"
                    />
                    <div className={`w-6 h-6 rounded ${theme.preview}`}></div>
                    <span className="text-sm font-medium text-gray-700">{theme.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Format Selection */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Format</h3>
              <div className="space-y-2">
                {[
                  { value: 'banner', label: 'Banner (16:9)', desc: 'Perfect for LinkedIn, Twitter headers' },
                  { value: 'square', label: 'Square (1:1)', desc: 'Instagram posts, profile pictures' },
                  { value: 'story', label: 'Story (9:16)', desc: 'Instagram/Facebook stories' }
                ].map((format) => (
                  <label key={format.value} className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="format"
                      value={format.value}
                      checked={config.format === format.value}
                      onChange={(e) => setConfig({ ...config, format: e.target.value })}
                      className="w-4 h-4 text-[#00529B] focus:ring-[#00529B] mt-1"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">{format.label}</span>
                      <p className="text-xs text-gray-500">{format.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Size Selection */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Size</h3>
              <div className="space-y-2">
                {[
                  { value: 'small', label: 'Small', desc: 'Compact for mobile sharing' },
                  { value: 'medium', label: 'Medium', desc: 'Balanced size for most uses' },
                  { value: 'large', label: 'Large', desc: 'High resolution for print' }
                ].map((size) => (
                  <label key={size.value} className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="size"
                      value={size.value}
                      checked={config.size === size.value}
                      onChange={(e) => setConfig({ ...config, size: e.target.value })}
                      className="w-4 h-4 text-[#00529B] focus:ring-[#00529B] mt-1"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">{size.label}</span>
                      <p className="text-xs text-gray-500">{size.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Display Options */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Display Options</h3>
              <div className="space-y-3">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.showTitle}
                    onChange={(e) => setConfig({ ...config, showTitle: e.target.checked })}
                    className="w-4 h-4 text-[#00529B] focus:ring-[#00529B] rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Show Job Title</span>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.showCompany}
                    onChange={(e) => setConfig({ ...config, showCompany: e.target.checked })}
                    className="w-4 h-4 text-[#00529B] focus:ring-[#00529B] rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Show Company</span>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.watermark}
                    onChange={(e) => setConfig({ ...config, watermark: e.target.checked })}
                    className="w-4 h-4 text-[#00529B] focus:ring-[#00529B] rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Show Conference Branding</span>
                </label>
              </div>
            </div>

            {/* Custom Text */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Custom Message</h3>
              <textarea
                value={config.customText}
                onChange={(e) => setConfig({ ...config, customText: e.target.value })}
                placeholder="I'll be speaking at (default)"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00529B] focus:border-transparent resize-none"
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-2">
                Leave empty to use default message
              </p>
            </div>
          </div>

          {/* Preview and Actions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Preview */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
              {previewUrl ? (
                <div className="flex justify-center bg-gray-100 rounded-lg p-8">
                  <iframe
                    src={previewUrl}
                    className="border-0 rounded-lg shadow-lg"
                    style={{
                      width: config.format === 'banner' ? '800px' : config.format === 'square' ? '500px' : '400px',
                      height: config.format === 'banner' ? '450px' : config.format === 'square' ? '500px' : '600px',
                      maxWidth: '100%'
                    }}
                    title="Banner Preview"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 bg-gray-100 rounded-lg">
                  <p className="text-gray-500">Select a speaker to see preview</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Cog6ToothIcon className="w-5 h-5 mr-2" />
                Share & Download
              </h3>

              {previewUrl && (
                <div className="space-y-4">
                  {/* Shareable URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shareable URL
                    </label>
                    <div className="flex">
                      <input
                        type="text"
                        value={generateShareableUrl()}
                        readOnly
                        className="flex-1 p-2 border border-gray-300 rounded-l-lg bg-gray-50 text-sm"
                      />
                      <button
                        onClick={handleCopyUrl}
                        className="px-4 py-2 bg-[#00529B] text-white rounded-r-lg hover:bg-[#003D74] transition-colors flex items-center"
                      >
                        <DocumentDuplicateIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to={previewUrl}
                      target="_blank"
                      className="inline-flex items-center px-4 py-2 bg-[#00529B] text-white rounded-lg hover:bg-[#003D74] transition-colors"
                    >
                      <EyeIcon className="w-4 h-4 mr-2" />
                      View Full Size
                    </Link>
                    
                    <button
                      onClick={handleDownload}
                      className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                      Download
                    </button>
                    
                    <button
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({
                            title: 'Speaker Banner',
                            url: generateShareableUrl()
                          });
                        } else {
                          handleCopyUrl();
                        }
                      }}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <ShareIcon className="w-4 h-4 mr-2" />
                      Share
                    </button>
                  </div>

                  {/* Usage Examples */}
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">Perfect for:</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• LinkedIn posts and profile updates</li>
                      <li>• Twitter announcements and headers</li>
                      <li>• Instagram posts and stories</li>
                      <li>• Email signatures and newsletters</li>
                      <li>• Company websites and press releases</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpeakerBannerGenerator;
